<template>
  <!-- Card > Ratings Overview -->
  <UiComponentWrapper
    v-if="ofType === 'overview'"
    ref="$el"
    class="card overview"
  >
    <UiComponentWrapper class="mobile">
      <UiComponentWrapper class="certifications">
        <UiComponentImage
          v-for="{ id, medal } in content.certifications"
          :key="`mobile-certification-${id}-${medalHeight}`"
          :alt="medal.alt"
          class="medal"
          :height="medal.height"
          :expected-size="medalHeight"
          :extra-params="{
            trim: 'color',
          }"
          :orientation="medal.width > medal.height ? 'portrait' : 'landscape'"
          :source="medal.url"
          use-natural-height
          use-natural-width
          :width="medal.width"
        />
      </UiComponentWrapper>
      <UiComponentWrapper class="ratings">
        <UiComponentWrapper class="overall">
          <UiComponentWrapper
            v-for="n in 5"
            :key="`star-${n}`"
            class="star"
            :style="{
              '--rating': 4.7 - n < 0 ? 1 - Math.abs(4.7 - n) : 1,
            }"
          />
        </UiComponentWrapper>
        <div
          class="from"
          v-html="`<p>${content.from} ${content.numbersOfReviews}</p>`"
        />
        <UiComponentWrapper class="customers">
          <UiComponentWrapper
            v-for="{ id, logo } in content.customers"
            :key="`mobile-customer-rating-${id}`"
            class="customer"
          >
            <UiComponentWrapper class="rating">
              <UiComponentWrapper
                v-for="n in 5"
                :key="`star-${n}`"
                class="star"
                :style="{
                  '--rating': 4.7 - n < 0 ? 1 - Math.abs(4.7 - n) : 1,
                }"
              />
            </UiComponentWrapper>
            <UiComponentImage
              :alt="logo.alt"
              class="logo"
              :height="logo.height"
              :expected-size="16"
              :extra-params="{
                con: 100,
                gam: 100,
                monochrome: '000000',
                sat: -100,
                trim: 'color',
                vib: 100,
              }"
              :orientation="logo.width > logo.height ? 'portrait' : 'landscape'"
              :source="logo.url"
              use-natural-height
              use-natural-width
              :width="logo.width"
            />
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentWrapper>
    <UiComponentWrapper class="desktop">
      <UiComponentWrapper class="certifications">
        <UiComponentImage
          v-for="({ id, medal }, index) in content.certifications"
          :key="`desktop-certification-${id}`"
          :alt="medal.alt"
          class="medal"
          :height="medal.height"
          :expected-size="[72, 90, 99, 90, 72][index]"
          :extra-params="{
            trim: 'color',
          }"
          :orientation="medal.width > medal.height ? 'portrait' : 'landscape'"
          :source="medal.url"
          use-natural-height
          use-natural-width
          :width="medal.width"
        />
      </UiComponentWrapper>
      <UiComponentWrapper class="ratings">
        <UiComponentWrapper class="overall">
          <UiComponentWrapper
            v-for="n in 5"
            :key="`star-${n}`"
            class="star"
            :style="{
              '--rating': 4.7 - n < 0 ? 1 - Math.abs(4.7 - n) : 1,
            }"
          />
        </UiComponentWrapper>
        <div
          class="from"
          v-html="`<p>${content.from} ${content.numbersOfReviews}</p>`"
        />
        <UiComponentWrapper class="customers">
          <UiComponentWrapper
            v-for="{ id, logo } in content.customers"
            :key="`desktop-customer-rating-${id}`"
            class="customer"
          >
            <UiComponentWrapper class="rating">
              <UiComponentWrapper
                v-for="n in 5"
                :key="`star-${n}`"
                class="star"
                :style="{
                  '--rating': 4.7 - n < 0 ? 1 - Math.abs(4.7 - n) : 1,
                }"
              />
            </UiComponentWrapper>
            <UiComponentImage
              :alt="logo.alt"
              class="logo"
              :height="logo.height"
              :expected-size="16"
              :extra-params="{
                con: 100,
                gam: 100,
                monochrome: '000000',
                sat: -100,
                trim: 'color',
                vib: 100,
              }"
              :orientation="logo.width > logo.height ? 'portrait' : 'landscape'"
              :source="logo.url"
              use-natural-height
              use-natural-width
              :width="logo.width"
            />
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
    </UiComponentWrapper>
  </UiComponentWrapper>
  <!-- End -->

  <!-- Card > Customers Showroom -->
  <UiComponentWrapper
    v-else-if="ofType === 'showroom'"
    ref="$el"
    class="card large showroom"
  >
    <UiComponentWrapper class="customers">
      <UiComponentWrapper
        v-for="({ customer: { id, logo } }, index) in content.showroom"
        :key="`showroom-logo-${id}`"
        class="logo"
        :class="{ active: index === showroomActiveIndex }"
        @click="showroomActiveIndex = index"
      >
        <UiComponentImage
          :key="`showroom-logo-${id}-${logoHeight}`"
          :alt="logo.alt"
          :height="logo.height"
          :expected-size="logoHeight"
          :extra-params="{
            trim: 'color',
          }"
          :orientation="logo.width > logo.height ? 'portrait' : 'landscape'"
          :source="logo.url"
          use-natural-height
          use-natural-width
          :width="logo.width"
        />
      </UiComponentWrapper>
    </UiComponentWrapper>
    <UiComponentWrapper class="slideshow">
      <UiComponentWrapper
        v-for="({ image }, index) in content.showroom"
        :key="`showroom-image-${index}`"
        class="image"
        :class="{ active: index === showroomActiveIndex }"
      >
        <UiComponentImage
          v-if="image && imageHeight && imageWidth"
          :alt="image.alt"
          :height="imageHeight"
          :expected-size="imageWidth"
          :extra-params="{
            fit: 'crop',
            h: imageHeight,
            w: imageWidth,
          }"
          :source="image.url"
          use-natural-width
          use-natural-height
          :width="imageWidth"
        />
      </UiComponentWrapper>
    </UiComponentWrapper>
  </UiComponentWrapper>
  <!-- End -->

  <!-- Card > Get Started -->
  <UiComponentWrapper
    v-else-if="ofType === 'get-started'"
    ref="$el"
    class="card get-started"
  >
    <UiComponentImage
      v-if="content.background && imageHeight && imageWidth"
      :alt="content.background.alt"
      class="background"
      :expected-size="imageWidth"
      :extra-params="{
        fit: 'crop',
        h: imageHeight,
        w: imageWidth,
      }"
      :height="imageHeight"
      :orientation="imageHeight > imageWidth ? 'portrait' : 'landscape'"
      :source="content.background.url"
      :width="imageWidth"
    />
    <UiComponentWrapper class="inner">
      <UiComponentHeading
        as="paragraph"
        :level="4"
      >
        {{ content.title }}
      </UiComponentHeading>
      <UiComponentParagraph>{{ content.text }}</UiComponentParagraph>
      <AtomButton
        :action="content.cta.action"
        label="Content card"
        :to="content.cta.to"
        type="text"
      >
        {{ content.cta.slot }}<UiComponentIcon type="arrow-right" />
      </AtomButton>
    </UiComponentWrapper>
  </UiComponentWrapper>
  <!-- End -->

  <!-- Card > Highlighted Classic Story -->
  <NuxtLink
    v-else-if="ofType === 'highlighted-story'"
    ref="$el"
    class="card highlighted-story large"
    :style="{
      backgroundColor: (content.color && content.color.hex) || '#0B42C3',
    }"
    :to="
      localePath({
        name: 'customers-slug',
        params: {
          slug: content.slug,
        },
      })
    "
  >
    <UiComponentWrapper class="inner">
      <UiComponentWrapper class="content">
        <UiComponentWrapper class="head">
          <UiComponentImage
            v-if="content.logo"
            :alt="content.logo.alt"
            class="logo"
            :height="content.logo.height"
            :expected-size="24"
            :extra-params="{
              trim: 'color',
            }"
            :orientation="
              content.logo.width > content.logo.height
                ? 'portrait'
                : 'landscape'
            "
            :source="content.logo.url"
            use-natural-height
            use-natural-width
            :width="content.logo.width"
          />
          <UiComponentParagraph v-if="content.highlightedKeyNumbers">
            <UiComponentIcon type="trending-up" />{{
              content.highlightedKeyNumbers
            }}
          </UiComponentParagraph>
        </UiComponentWrapper>
        <UiComponentWrapper class="body">
          “{{ content.quote }}”
        </UiComponentWrapper>
        <UiComponentWrapper class="footer">
          <UiComponentImage
            v-if="content.avatar"
            :alt="content.avatar.alt"
            class="avatar"
            :height="content.avatar.height"
            :expected-size="48"
            :source="content.avatar.url"
            :width="content.avatar.width"
          />
          <UiComponentWrapper class="author">
            <UiComponentParagraph>{{ content.author }}</UiComponentParagraph>
            <UiComponentParagraph>{{ content.position }}</UiComponentParagraph>
          </UiComponentWrapper>
        </UiComponentWrapper>
      </UiComponentWrapper>
      <UiComponentWrapper
        class="media"
        :class="{
          'is-picture': !content.video && content.defaultBackground,
          'is-video': content.video,
        }"
      >
        <OrganismPlayerVideo
          v-if="content.video"
          :poster-src="content.video.asset.thumbnailUrl"
          :video-src="content.video.asset.streamingUrl"
        />
        <UiComponentImage
          v-else-if="content.defaultBackground"
          :alt="content.defaultBackground.alt"
          :expected-size="defaultBackgroundSize"
          :height="content.defaultBackground.height"
          orientation="portrait"
          :source="content.defaultBackground.url"
          :width="content.defaultBackground.width"
        />
      </UiComponentWrapper>
    </UiComponentWrapper>
  </NuxtLink>
  <!-- End -->

  <!-- Card > Classic Story -->
  <NuxtLink
    v-else-if="ofType === 'classic-story'"
    ref="$el"
    class="card classic-story"
    :style="{
      background: `linear-gradient(0deg, #FFFFFF 0%, ${
        (content.color && content.color.hex) || '#0B42C3'
      } 100%)`,
    }"
    :to="
      localePath({
        name: 'customers-slug',
        params: {
          slug: content.slug,
        },
      })
    "
  >
    <UiComponentWrapper class="inner">
      <UiComponentWrapper class="head">
        <UiComponentImage
          v-if="content.logo"
          :alt="content.logo.alt"
          class="logo"
          :height="content.logo.height"
          :expected-size="24"
          :extra-params="{
            trim: 'color',
          }"
          :orientation="
            content.logo.width > content.logo.height ? 'portrait' : 'landscape'
          "
          :source="content.logo.url"
          use-natural-height
          use-natural-width
          :width="content.logo.width"
        />
        <UiComponentParagraph v-if="content.highlightedKeyNumbers">
          <UiComponentIcon type="trending-up" /><span>{{ content.highlightedKeyNumbers }}</span>
        </UiComponentParagraph>
      </UiComponentWrapper>
      <UiComponentWrapper class="body">
        <UiComponentHeading
          as="paragraph"
          :level="5"
        >
          {{ content.title }}
        </UiComponentHeading>
        <AtomButton type="text">
          {{ content.readMore }}<UiComponentIcon type="arrow-right" />
        </AtomButton>
      </UiComponentWrapper>
    </UiComponentWrapper>
  </NuxtLink>
  <!-- End -->

  <!-- Card > Example Story -->
  <NuxtLink
    v-else-if="ofType === 'example-story'"
    ref="$el"
    class="card example-story"
    :to="
      localePath({
        name: 'customers-slug',
        params: {
          slug: content.slug,
        },
      })
    "
  >
    <UiComponentWrapper class="inner">
      <UiComponentWrapper class="head">
        <UiComponentImage
          v-if="content.image && imageHeight && imageWidth"
          :alt="content.image.alt"
          class="image"
          :height="imageHeight"
          :expected-size="imageWidth"
          :extra-params="{
            fit: 'crop',
            h: imageHeight,
            w: imageWidth,
          }"
          :source="content.image.url"
          :width="imageWidth"
        />
      </UiComponentWrapper>
      <UiComponentWrapper class="body">
        <UiComponentHeading
          as="paragraph"
          :level="5"
        >
          {{ content.title }}
        </UiComponentHeading>
        <AtomButton type="text">
          {{ content.readMore }}<UiComponentIcon type="arrow-right" />
        </AtomButton>
      </UiComponentWrapper>
    </UiComponentWrapper>
  </NuxtLink>
  <!-- End -->

  <!-- Card > Review G2 -->
  <UiComponentWrapper
    v-else-if="ofType === 'review'"
    ref="$el"
    class="card review"
    :class="{ 'hover': overlayIsVisible, 'is-hoverable': canOverflown }"
    @mouseenter="$device.isDesktop ? (overlayIsVisible = true) : null"
    @mouseleave="$device.isDesktop ? (overlayIsVisible = false) : null"
    @click="
      $device.isMobileOrTablet ? (overlayIsVisible = !overlayIsVisible) : null
    "
  >
    <UiComponentWrapper class="inner">
      <UiComponentWrapper class="head">
        <UiComponentWrapper class="rating">
          <UiComponentWrapper
            v-for="n in 5"
            :key="`star-${n}`"
            class="star"
            :style="{
              '--rating':
                Number.parseFloat(content.rating) - n < 0
                  ? 1 - Math.abs(content.rating - n)
                  : 1,
            }"
          />
        </UiComponentWrapper>
        <UiComponentParagraph
          v-if="content.position"
          class="position"
        >
          {{
            content.position
          }}
        </UiComponentParagraph>
      </UiComponentWrapper>
      <UiComponentWrapper class="body">
        <UiComponentParagraph class="enterprise">
          {{
            content.enterprise.type
          }}
        </UiComponentParagraph>
        <UiComponentParagraph class="quote">
          {{ content.quote }}
        </UiComponentParagraph>
      </UiComponentWrapper>
      <UiComponentWrapper class="footer">
        <UiComponentImage
          v-if="content.avatar"
          :alt="content.avatar.alt"
          class="avatar"
          :height="content.avatar.height"
          :expected-size="48"
          :source="content.avatar.url"
          :width="content.avatar.width"
        />
        <UiComponentParagraph class="enterprise">
          {{ content.author }}
        </UiComponentParagraph>
      </UiComponentWrapper>
    </UiComponentWrapper>
    <UiComponentWrapper class="overlay">
      <UiComponentWrapper class="head">
        <UiComponentWrapper class="rating">
          <UiComponentWrapper
            v-for="n in 5"
            :key="`star-${n}`"
            class="star"
            :style="{
              '--rating':
                Number.parseFloat(content.rating) - n < 0
                  ? 1 - Math.abs(content.rating - n)
                  : 1,
            }"
          />
        </UiComponentWrapper>
        <UiComponentParagraph
          v-if="content.position"
          class="position"
        >
          {{
            content.position
          }}
        </UiComponentParagraph>
      </UiComponentWrapper>
      <UiComponentWrapper class="body">
        <UiComponentParagraph class="enterprise">
          {{
            content.enterprise.type
          }}
        </UiComponentParagraph>
        <UiComponentParagraph class="quote">
          {{ content.quote }}
        </UiComponentParagraph>
      </UiComponentWrapper>
      <UiComponentWrapper class="footer">
        <UiComponentImage
          v-if="content.avatar"
          :alt="content.avatar.alt"
          class="avatar"
          :height="content.avatar.height"
          :expected-size="48"
          :source="content.avatar.url"
          :width="content.avatar.width"
        />
        <UiComponentParagraph class="enterprise">
          {{ content.author }}
        </UiComponentParagraph>
      </UiComponentWrapper>
    </UiComponentWrapper>
  </UiComponentWrapper>
  <!-- End -->
</template>

<script setup>
// Props
const props = defineProps({
  content: {
    required: true,
    type: Object,
  },
  ofType: {
    default() {
      return 'classic-story'
    },
    required: false,
    type: String,
    validator(value) {
      return (
        [
          'classic-story',
          'example-story',
          'get-started',
          'highlighted-story',
          'overview',
          'review',
          'showroom',
        ].indexOf(value) !== -1
      )
    },
  },
  screenWidth: {
    default() {
      return 360
    },
    required: false,
    type: Number,
  },
})

// Ref
const $el = ref(null)
const canOverflown = ref(false)
const imageHeight = ref(null)
const imageWidth = ref(null)
const observer = ref(null)
const overlayIsVisible = ref(false)
const showroomActiveIndex = ref(0)

// Computed Properties
const defaultBackgroundSize = computed(() => {
  return props.screenWidth < 768 ? 127 : 219
})

const logoHeight = computed(() => {
  return props.screenWidth < 768 ? 12 : 20
})

const medalHeight = computed(() => {
  return props.screenWidth < 768 ? 56 : 72
})

// Methods
function onObserve() {
  if (props.ofType === 'example-story') {
    imageHeight.value = $el.value.querySelector('.head').offsetHeight
    imageWidth.value = $el.value.querySelector('.head').offsetWidth
  }

  if (props.ofType === 'get-started') {
    imageHeight.value = $el.value.$el.offsetHeight
    imageWidth.value = $el.value.$el.offsetWidth
  }

  if (props.ofType === 'review') {
    canOverflown.value = $el.value.$el.querySelector('.overlay .body .quote').offsetHeight >= 112
  }

  if (props.ofType === 'showroom') {
    imageHeight.value = $el.value.$el.querySelector('.slideshow').offsetHeight
    imageWidth.value = $el.value.$el.querySelector('.slideshow').offsetWidth
  }
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  if (observer.value) {
    observer.value.unobserve($el.value.$el)
  }
})

onMounted(() => {
  nextTick(() => {
    if ($el.value) {
      observer.value = new ResizeObserver(onObserve)
      observer.value.observe($el.value.$el)
    }
  })
})
</script>

<style lang="postcss" scoped>
.card {
  &.overview {
    .mobile {
      @apply flex-col;

      @screen lg {
        @apply hidden;
      }

      .certifications {
        @apply justify-between p-8 bg-winter-green-100 rounded-3xl;

        .medal {
          @apply flex-shrink-0 rounded-none;

          &:first-child {
            @apply ml-0;
          }

          &:last-child {
            @apply mr-0;
          }
        }
      }

      .ratings {
        @apply flex-col items-center mt-10 p-6 bg-sandstorm-yellow-100 rounded-3xl;

        .overall {
          @apply w-auto;

          .star {
            @apply relative w-6 h-6 mx-px;
            background: #c0c0c5;
            mask-image: url(@/assets/svg/star-empty.svg);
            mask-repeat: no-repeat;
            mask-size: 24px;

            &:first-child {
              @apply ml-0;
            }

            &:last-child {
              @apply mr-0;
            }

            &::after {
              @apply absolute h-6;
              background: #ffd23f;
              content: "";
              width: calc(var(--rating) * 100%);
            }
          }
        }

        .from {
          @apply flex mt-2 whitespace-pre-wrap;

          :deep(p) {
            @apply inline text-grey-blue-800 text-origin leading-7;

            strong {
              @apply font-medium;
            }
          }
        }

        .customers {
          @apply flex-wrap justify-center mt-8;

          .customer {
            @apply flex-col items-center mx-3 mt-8;
            width: calc((100% - 48px) / 3);

            @screen md {
              width: calc((100% - 120px) / 6);
            }

            &:nth-child(1),
            &:nth-child(2),
            &:nth-child(3) {
              @apply mt-0;
            }

            &:nth-child(4),
            &:nth-child(5),
            &:nth-child(6) {
              @screen md {
                @apply mt-0;
              }
            }

            &:nth-child(3n + 1) {
              @apply ml-0;

              @screen md {
                @apply mx-3;
              }
            }

            &:nth-child(3n + 3) {
              @apply mr-0;

              @screen md {
                @apply mx-3;
              }
            }

            &:nth-child(6n + 1) {
              @screen md {
                @apply ml-0 !important;
              }
            }

            &:nth-child(6n + 6) {
              @screen md {
                @apply mr-0 !important;
              }
            }

            .rating {
              @apply w-auto;

              .star {
                @apply relative w-3 h-3 mx-px;
                background: #c0c0c5;
                mask-image: url(@/assets/svg/star-empty.svg);
                mask-repeat: no-repeat;
                mask-size: 12px;

                &:first-child {
                  @apply ml-0;
                }

                &:last-child {
                  @apply mr-0;
                }

                &::after {
                  @apply absolute h-3;
                  background: #ffd23f;
                  content: "";
                  width: calc(var(--rating) * 100%);
                }
              }
            }

            .logo {
              @apply mt-2 rounded-none;
              filter: brightness(0) saturate(100%) invert(44%) sepia(9%)
                saturate(732%) hue-rotate(163deg) brightness(92%) contrast(96%);
            }
          }
        }
      }
    }

    .desktop {
      @apply relative hidden;

      @screen lg {
        @apply flex flex-col items-center px-[44px] pt-12 pb-8 bg-winter-green-100 transition-colors duration-800 ease-in-out;
      }

      &:hover {
        @apply bg-sandstorm-yellow-100;

        &::after {
          @apply opacity-0;
        }

        .certifications {
          @apply opacity-0 -translate-y-1/2;
        }

        .ratings {
          @apply -translate-y-[162px];
        }
      }

      &::after {
        @apply absolute bottom-0 h-[100px] w-full opacity-100 content-[''] transition-opacity duration-800 ease-in-out;
        background: linear-gradient(
          180deg,
          rgba(241, 247, 249, 0) 0%,
          #f1f7f9 100%
        );
      }

      .certifications {
        @apply relative h-[99px] w-[268px] transform translate-y-0 opacity-100 transition-all duration-800 ease-in-out;

        .medal {
          @apply absolute top-1/2 transform -translate-y-1/2 rounded-none;

          &:nth-child(1) {
            @apply left-0 z-0;
          }

          &:nth-child(2) {
            @apply left-[38px] z-10;
          }

          &:nth-child(3) {
            @apply left-1/2 -translate-x-1/2 z-20;
            filter: drop-shadow(0px 11.562px 23.1241px rgba(13, 22, 38, 0.04))
              drop-shadow(0px 5.78102px 11.562px rgba(13, 22, 38, 0.04))
              drop-shadow(0px 2.89051px 5.78102px rgba(13, 22, 38, 0.04))
              drop-shadow(0px 1.44526px 2.89051px rgba(13, 22, 38, 0.04))
              drop-shadow(0px 0.722628px 1.44526px rgba(13, 22, 38, 0.04));
          }

          &:nth-child(4) {
            @apply right-[38px] z-10;
          }

          &:nth-child(5) {
            @apply right-0 z-0;
          }
        }
      }

      .ratings {
        @apply flex-col items-center h-[248px] mt-10 transform translate-y-0 transition-transform duration-800 ease-in-out;

        .overall {
          @apply w-auto;

          .star {
            @apply relative w-6 h-6 mx-px;
            background: #c0c0c5;
            mask-image: url(@/assets/svg/star-empty.svg);
            mask-repeat: no-repeat;
            mask-size: 24px;

            &:first-child {
              @apply ml-0;
            }

            &:last-child {
              @apply mr-0;
            }

            &::after {
              @apply absolute h-6;
              background: #ffd23f;
              content: "";
              width: calc(var(--rating) * 100%);
            }
          }
        }

        .from {
          @apply flex mt-2 whitespace-pre-wrap;

          :deep(p) {
            @apply inline text-grey-blue-800 text-origin leading-7;

            strong {
              @apply font-medium;
            }
          }
        }

        .customers {
          @apply flex-wrap mt-8;

          .customer {
            @apply flex-col items-center mx-3 mt-8;
            width: calc((100% - 48px) / 3);

            &:nth-child(1),
            &:nth-child(2),
            &:nth-child(3) {
              @apply mt-0;
            }

            &:nth-child(3n + 1) {
              @apply ml-0;
            }

            &:nth-child(3n + 3) {
              @apply mr-0;
            }

            .rating {
              @apply w-auto;

              .star {
                @apply relative w-3 h-3 mx-px;
                background: #c0c0c5;
                mask-image: url(@/assets/svg/star-empty.svg);
                mask-repeat: no-repeat;
                mask-size: 12px;

                &:first-child {
                  @apply ml-0;
                }

                &:last-child {
                  @apply mr-0;
                }

                &::after {
                  @apply absolute h-3;
                  background: #ffd23f;
                  content: "";
                  width: calc(var(--rating) * 100%);
                }
              }
            }

            .logo {
              @apply mt-2 rounded-none;
              filter: brightness(0) saturate(100%) invert(44%) sepia(9%)
                saturate(732%) hue-rotate(163deg) brightness(92%) contrast(96%);
            }
          }
        }
      }
    }
  }

  &.showroom {
    @apply flex-col p-6 border-2 border-grey-blue-200;

    @screen md {
      @apply flex-row flex-wrap items-center p-8;
    }

    @screen lg {
      @apply h-122 py-0;
    }

    .customers {
      @screen md {
        @apply flex-col flex-shrink-0 w-[148px];
      }

      .logo {
        @apply w-auto mx-2 cursor-pointer;

        @screen md {
          @apply items-center h-10 mx-0 my-2;
        }

        &:first-child {
          @apply ml-0;

          @screen md {
            @apply mt-0;
          }
        }

        &:last-child {
          @apply mr-0;

          @screen md {
            @apply mb-0;
          }
        }

        &:hover {
          picture {
            filter: grayscale(0);
          }
        }

        &.active {
          @apply cursor-default;

          picture {
            filter: grayscale(0);
          }
        }

        picture {
          @apply rounded-none duration-800 ease-in-out;
          transition-property: filter;
          filter: brightness(0) saturate(100%) invert(69%) sepia(15%)
            saturate(445%) hue-rotate(163deg) brightness(82%) contrast(81%);
        }
      }
    }

    .slideshow {
      @apply relative w-full mt-6 shadow-light4 rounded-xl;
      padding-bottom: calc(100% * (206 / 295));

      @screen md {
        @apply flex-grow w-auto mt-0 ml-10;
        padding-bottom: calc((100% - 148px - 40px) * (416 / 584));
      }

      .image {
        @apply absolute inset-0 opacity-0 rounded-xl overflow-hidden transition-opacity duration-800 ease-in-out;

        &.active {
          @apply opacity-100;
        }

        picture {
          @apply rounded-none;
        }
      }
    }
  }

  &.get-started {
    @apply relative items-center cursor-pointer;

    &:hover {
      .inner {
        .button {
          :deep(i) {
            @apply translate-x-2;
          }
        }
      }
    }

    .background {
      @apply absolute inset-0 bg-livestorm-blue-700 rounded-none z-0;
    }

    .inner {
      @apply flex-col px-8 z-10;

      .h4 {
        @apply text-white;
      }

      .h4 + p {
        @apply mt-4 text-white text-origin leading-7;
      }

      .button {
        @apply self-start h-[50px] mt-2 px-0 font-bold text-white;

        i {
          @apply text-white text-base leading-none;
        }
      }
    }
  }

  &.highlighted-story {
    @apply border-2 border-grey-blue-200 transition-all duration-500 ease-out overflow-visible !important;

    @screen md {
      @apply overflow-hidden !important;
    }

    &:hover {
      @apply shadow-light4 overflow-hidden;
    }

    .inner {
      @apply relative flex-col mt-[6px] px-8 pt-8 pb-6 bg-white rounded-t-3xl rounded-b-[22px];

      @screen md {
        @apply flex-row items-center rounded-b-none;
      }

      .content {
        @apply flex-col z-10;

        @screen md {
          @apply flex-shrink-0 max-w-[360px];
        }

        @screen lg {
          @apply max-w-[478px];
        }

        .head {
          @apply flex-col self-start items-center min-h-[40px] w-auto;

          .logo {
            @apply self-start rounded-none;
          }

          p {
            @apply inline-flex items-center mt-2 font-medium text-spring-green-600 text-sm leading-7;

            i {
              @apply mr-2 text-spring-green-600 text-base leading-none;
            }
          }
        }

        .body {
          @apply flex-col mt-4 text-grey-blue-900 text-base leading-6;
        }

        .footer {
          @apply items-center mt-4;

          .avatar {
            @apply flex-shrink-0 mr-4 rounded-full;
          }

          .author {
            @apply flex-col;

            p {
              @apply font-medium text-winter-green-900 text-origin leading-7;
            }

            p + p {
              @apply font-normal text-grey-blue-600 text-sm leading-6;
            }
          }
        }
      }

      .media {
        @apply mt-6 z-0;

        @screen md {
          @apply justify-end w-auto ml-10 mt-0;
        }

        &.is-picture {
          @apply absolute w-auto -right-[22.46px] -top-[60px];

          @screen md {
            @apply static;
          }
        }

        :deep(.video) {
          @apply flex self-start h-full;

          .controls,
          .gradient {
            @apply hidden !important;
          }

          video {
            @apply self-start h-auto !important;
          }
        }

        picture {
          @apply rounded-none;
        }
      }
    }
  }

  &.classic-story {
    @apply border-2 border-grey-blue-200 transition-all duration-500 ease-out;

    &:hover {
      @apply shadow-light4;

      .body {
        .button {
          :deep(i) {
            @apply translate-x-2;
          }
        }
      }
    }

    .inner {
      @apply flex-col h-full mt-[6px] px-8 pt-8 pb-6 bg-white rounded-t-3xl rounded-b-none;

      .head {
        @apply flex-col min-h-[60px];

        .logo {
          @apply rounded-none;
        }

        p {
          @apply inline-flex items-center mt-2 font-medium text-spring-green-600 text-sm leading-7;

          i {
            @apply mr-2 text-spring-green-600 text-base leading-none;
          }

          span {
            @apply line-clamp-1;
          }
        }
      }

      .body {
        @apply flex-col h-full mt-2;

        .h5 {
          @apply text-ellipsis overflow-hidden;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
        }

        .button {
          @apply self-start mt-auto font-bold px-0 h-[50px] leading-none;
        }
      }
    }
  }

  &.example-story {
    @apply transition-all duration-500 ease-out;

    &:hover {
      @apply shadow-light4;

      .inner {
        .body {
          .button {
            :deep(i) {
              @apply translate-x-2;
            }
          }
        }
      }
    }

    .inner {
      @apply flex-col px-8 pt-8 pb-6 bg-livestorm-blue-100;

      .head {
        @apply relative flex-col min-h-[94px] max-h-[94px] h-full z-0;

        &::after {
          @apply absolute -bottom-3 left-0 right-0 h-[46px] content-[''];
          background: linear-gradient(
            180deg,
            rgba(240, 244, 255, 0) 0%,
            #f0f4ff 34.48%
          );
        }

        .image {
          @apply rounded-xs;
        }
      }

      .body {
        @apply flex-col h-full z-10;

        .h5 {
          @apply text-ellipsis overflow-hidden;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
        }

        .button {
          @apply self-start mt-auto font-bold px-0 h-[50px] leading-none;
        }
      }
    }
  }

  &.review {
    @apply relative rounded-none overflow-visible !important;

    &.is-hoverable {
      &.hover {
        .overlay {
          @apply opacity-100 shadow-light4 visible;
        }
      }
    }

    .inner {
      @apply flex-col p-8 bg-sunburst-red-100 rounded-3xl z-0;

      .head {
        @apply items-center;

        .rating {
          @apply w-auto;

          .star {
            @apply relative w-5 h-5 mx-px;
            background: #c0c0c5;
            mask-image: url(@/assets/svg/star-empty.svg);
            mask-repeat: no-repeat;
            mask-size: 20px;

            &:first-child {
              @apply ml-0;
            }

            &:last-child {
              @apply mr-0;
            }

            &::after {
              @apply absolute h-5;
              background: #ffd23f;
              content: "";
              width: calc(var(--rating) * 100%);
            }
          }
        }

        .position {
          @apply text-grey-blue-900 ml-4 text-sm leading-5;
        }
      }

      .body {
        @apply flex-col mt-3;

        .enterprise {
          @apply text-grey-blue-600 text-sm leading-[18px];
        }

        .quote {
          @apply mt-3 text-ellipsis text-grey-blue-900 text-base leading-7 overflow-hidden;
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
        }
      }

      .footer {
        @apply items-center mt-3;

        .avatar {
          @apply flex-shrink-0 mr-3 rounded-full;
        }

        p {
          @apply font-medium text-winter-green-900 text-origin leading-7;
        }
      }
    }

    .overlay {
      @apply absolute flex-col p-8 bg-sunburst-red-100 opacity-0 invisible rounded-3xl z-10 transition-all duration-500 ease-in-out;

      .head {
        @apply items-center;

        .rating {
          @apply w-auto;

          .star {
            @apply relative w-5 h-5 mx-px;
            background: #c0c0c5;
            mask-image: url(@/assets/svg/star-empty.svg);
            mask-repeat: no-repeat;
            mask-size: 20px;

            &:first-child {
              @apply ml-0;
            }

            &:last-child {
              @apply mr-0;
            }

            &::after {
              @apply absolute h-5;
              background: #ffd23f;
              content: "";
              width: calc(var(--rating) * 100%);
            }
          }
        }

        .position {
          @apply text-grey-blue-900 ml-4 text-sm leading-5;
        }
      }

      .body {
        @apply flex-col mt-3;

        .enterprise {
          @apply text-grey-blue-600 text-sm leading-[18px];
        }

        .quote {
          @apply mt-3 text-grey-blue-900 text-base leading-7;
        }
      }

      .footer {
        @apply items-center mt-3;

        .avatar {
          @apply flex-shrink-0 mr-3 rounded-full;
        }

        p {
          @apply font-medium text-winter-green-900 text-origin leading-7;
        }
      }
    }
  }
}
</style>
